'use client'

import { CartItemList } from '@/feature/cart/components/cart-item-list/CartItemList'
import {
  ProductRow,
  ProductRowSource,
} from '@/feature/product/components/product-row/ProductRow.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Drawer, DrawerBody, DrawerFooter } from '@/ui/drawer/drawer/Drawer.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Link } from '@/ui/links/link/Link.client'
import { gql } from '@gql-generated/gql'
import type { MiniCart_ItemFragment } from '@gql-generated/graphql'
import { toCheckoutCart } from '../../services/cart.routing'
import { CartSummaryPricing } from '../cart-summary-pricing/CartSummaryPricing.client'

gql(/* GraphQL */ `
  fragment MiniCart_Cart on Cart {
    id
    totalQuantity
    totalExclTax
    totalDiscount
    totalEcoPart
    items {
      ...MiniCart_Item
    }
  }

  fragment MiniCart_Item on CartItem {
    id
    quantity
    totalExclTax
    unitPrice {
      exclTax
    }
    product {
      reference
      name
      type
      urlKey
      mainImage {
        ...FullPicture
      }
      brand {
        name
        urlKey
      }
      stock {
        availability
        isPriceAvailable
        isSellable
        packaging {
          ...FullPackaging
        }
      }
      canAddToCart
      clickAndCollectStatus
    }
    discount {
      catalogDiscountAmount
      percentage
      webDiscount {
        amount
        hasCoupon
        label
      }
    }
    promotionTag {
      label
      type
    }
  }
`)

gql(/* GraphQL */ `
  fragment MiniCart_ShippingFee on ShippingFee {
    ... on ShippingFeeFree {
      isFranco
    }
    ... on ShippingFeeWithAmount {
      amount
    }
  }
`)

export function MiniCart({
  isDrawerOpen,
  onOpenChangeAction,
  items,
  totalQuantity,
}: {
  isDrawerOpen: boolean
  onOpenChangeAction: (isOpen: boolean) => void
  items: MiniCart_ItemFragment[]
  totalQuantity: number
}) {
  const { t } = useTranslation(['miniCart', 'generic'])

  return (
    <Drawer
      size="xl"
      isOpen={!!isDrawerOpen}
      onOpenChange={onOpenChangeAction}
      title={t('miniCart:title')}
      subtitle={t('generic:products', { count: totalQuantity })}
    >
      <DrawerBody>
        <div className="flex h-full flex-col">
          <div className="flex h-full flex-col justify-between gap-2">
            <CartItemList withDividers className="mb-1 w-full gap-2">
              {items?.map(
                (cartItem) =>
                  cartItem?.product && (
                    <CartItemList.Item key={cartItem.id}>
                      <ProductRow
                        id={cartItem.id}
                        quantity={cartItem.quantity}
                        totalExclTax={cartItem.totalExclTax}
                        unitPrice={cartItem.unitPrice}
                        product={{ ...cartItem.product, isLinkedProductsLinkVisible: false }}
                        discount={cartItem.discount}
                        promotionTag={cartItem.promotionTag}
                        source={ProductRowSource.MINI_CART}
                        displayReadOnlyQuantity
                        displayQuantityPicker={false}
                      />
                    </CartItemList.Item>
                  ),
              )}
            </CartItemList>
          </div>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <CartSummaryPricing className="p-0">
          <Link
            bold
            variant="button-primary"
            size="lg"
            href={toCheckoutCart}
            onPress={() => onOpenChangeAction(false)}
            trackingData={{
              event: 'gaEvent',
              eventCategory: 'mini-panier',
              eventAction: 'cta',
              eventLabel: 'voir mon panier',
            }}
            className="w-full"
            iconLeft={<Icon.ShoppingBasket />}
          >
            {t('miniCart:seeCart')}
          </Link>
        </CartSummaryPricing>
      </DrawerFooter>
    </Drawer>
  )
}
